import { useState, useEffect } from 'react';
import { useDataEngine } from '@dhis2/app-runtime';

const WEIGHT_CONFIG_NAMESPACE = 'appraisal-weight-config';

// Default weights (50/50 split)
const getDefaultWeights = () => ({
  businessObjectives: {
    weight: 50,
    label: 'Business Objectives',
    description: 'SMART objectives and KPIs achievement',
    isActive: true
  },
  leadershipBehavioural: {
    weight: 50,
    label: 'Leadership & Behavioural',
    description: 'Leadership competencies and behavioral traits',
    isActive: true
  },
  total: 100
});

const getDataStoreKey = (orgId) => orgId ? `weights_${orgId}` : 'weights_global';

const getParentOrgUnitId = async (engine, orgUnitId) => {
  if (!orgUnitId) return null;
  try {
    const response = await engine.query({
      orgUnit: {
        resource: `organisationUnits/${orgUnitId}`,
        params: { fields: 'path,parent[id]' }
      }
    });
    const path = response.orgUnit?.path;
    if (path) {
      const pathParts = path.split('/').filter(Boolean);
      if (pathParts.length >= 5) return pathParts[4];
      return response.orgUnit?.parent?.id || null;
    }
    return null;
  } catch (error) {
    console.error('Failed to get parent org unit:', error);
    return null;
  }
};

export const useWeightConfig = (orgUnitId) => {
  const engine = useDataEngine();
  const [weightConfig, setWeightConfig] = useState(getDefaultWeights());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadWeightConfig = async () => {
      if (!orgUnitId) {
        setLoading(false);
        return;
      }

      try {
        const parentOrgId = await getParentOrgUnitId(engine, orgUnitId);
        const orgsToCheck = [orgUnitId];
        if (parentOrgId && parentOrgId !== orgUnitId) orgsToCheck.push(parentOrgId);

        let loadedConfig = null;
        
        for (const orgId of orgsToCheck) {
          const key = getDataStoreKey(orgId);
          try {
            const response = await engine.query({
              weightConfig: {
                resource: `dataStore/${WEIGHT_CONFIG_NAMESPACE}/${key}`,
              }
            });
            const data = response.weightConfig;
            if (data && typeof data === 'object' && data.businessObjectives && data.leadershipBehavioural) {
              loadedConfig = data;
              console.log(`Loaded weight config from ${orgId}:`, loadedConfig);
              break;
            }
          } catch (err) {
            // Continue to next org
          }
        }

        if (!loadedConfig) {
          try {
            const globalResponse = await engine.query({
              weightConfig: {
                resource: `dataStore/${WEIGHT_CONFIG_NAMESPACE}/${getDataStoreKey(null)}`,
              }
            });
            loadedConfig = globalResponse.weightConfig;
          } catch (err) {
            // Use defaults
          }
        }

        if (loadedConfig && loadedConfig.businessObjectives && loadedConfig.leadershipBehavioural) {
          // Validate weights total to 100
          const total = (loadedConfig.businessObjectives.weight || 0) + (loadedConfig.leadershipBehavioural.weight || 0);
          if (total !== 100) {
            console.warn(`Weight total is ${total}, adjusting to 100`);
            // Adjust proportionally
            const businessWeight = loadedConfig.businessObjectives.weight || 50;
            const leadershipWeight = loadedConfig.leadershipBehavioural.weight || 50;
            const factor = 100 / (businessWeight + leadershipWeight);
            loadedConfig.businessObjectives.weight = Math.round(businessWeight * factor);
            loadedConfig.leadershipBehavioural.weight = Math.round(leadershipWeight * factor);
          }
          setWeightConfig(loadedConfig);
        } else {
          setWeightConfig(getDefaultWeights());
        }
      } catch (err) {
        console.error('Failed to load weight config:', err);
        setError(err);
        setWeightConfig(getDefaultWeights());
      } finally {
        setLoading(false);
      }
    };

    loadWeightConfig();
  }, [engine, orgUnitId]);

  const updateWeightConfig = async (newConfig) => {
    try {
      const key = getDataStoreKey(orgUnitId);
      await engine.mutate({
        resource: `dataStore/${WEIGHT_CONFIG_NAMESPACE}/${key}`,
        type: 'update',
        data: newConfig
      });
      setWeightConfig(newConfig);
      return true;
    } catch (error) {
      console.error('Failed to update weight config:', error);
      return false;
    }
  };

  return { weightConfig, loading, error, updateWeightConfig };
};